import React from "react";

interface ProcessStep {
  title: string;
  description: string;
  icon: string;
  color: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  steps?: string | ProcessStep[];
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultSteps: ProcessStep[] = [
  {
    title: "",
    description: "",
    icon: "1️⃣",
    color: "#22c55e",
  },
  {
    title: "",
    description: "",
    icon: "2️⃣",
    color: "#16a34a",
  },
  {
    title: "",
    description: "",
    icon: "3️⃣",
    color: "#15803d",
  },
  {
    title: "",
    description: "",
    icon: "4️⃣",
    color: "#166534",
  },
];

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const ProcessSlide1 = {
  id: "process-slide1",
  name: "Process Style 1",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "steps",
      label: "Steps",
      type: "text",
      defaultValue: JSON.stringify(defaultSteps),
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      steps = JSON.stringify(defaultSteps),
      bgColor = "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
      onEdit = () => {},
    } = content;

    let parsedSteps = defaultSteps;
    try {
      if (typeof steps === "string") {
        parsedSteps = JSON.parse(steps);
      } else if (Array.isArray(steps)) {
        parsedSteps = steps;
      }
    } catch (error) {
      console.warn("Error parsing steps:", error);
    }

    const handleStepEdit = (index: number, field: string, value: string) => {
      const updatedSteps = [...parsedSteps];
      updatedSteps[index] = {
        ...updatedSteps[index],
        [field]: value,
      };
      onEdit({ ...content, steps: JSON.stringify(updatedSteps) });
    };

    return (
      <div className="w-full h-full relative overflow-hidden text-white">
        {/* Dynamic Background */}
        <div
          className="absolute inset-0 transition-all duration-1000"
          style={{ background: bgColor }}
        >
          {/* Animated Background Patterns */}
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute w-full h-full"
              style={{
                backgroundImage: `
                  radial-gradient(circle at 20% 20%, ${parsedSteps[0].color}80 0%, transparent 25%),
                  radial-gradient(circle at 80% 80%, ${parsedSteps[1].color}80 0%, transparent 25%),
                  radial-gradient(circle at 50% 50%, ${parsedSteps[2].color}80 0%, transparent 35%)
                `,
              }}
            />
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-8">
          {/* Header Section */}
          <div className="text-center mb-12">
            <EditableDiv
              text={title}
              onEdit={(value) => onEdit({ ...content, title: value })}
              className="text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r 
                        from-white via-green-100 to-white animate-text-shine"
            />
            <EditableDiv
              text={subtitle}
              onEdit={(value) => onEdit({ ...content, subtitle: value })}
              className="text-xl text-green-100/90 font-light"
            />
          </div>

          {/* Process Steps */}
          <div className="flex-1 flex items-center justify-center">
            <div className="grid grid-cols-4 gap-8 px-8">
              {parsedSteps.map((step, idx) => (
                <div key={idx} className="relative group">
                  {/* Step Card */}
                  <div
                    className="relative bg-white/5 backdrop-blur-md rounded-2xl p-6
                            transform transition-all duration-500 hover:scale-105
                            border border-white/10 hover:border-white/30
                            hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]"
                    style={{
                      background: `linear-gradient(165deg, 
                        ${step.color}20 0%, 
                        ${step.color}10 40%, 
                        transparent 100%
                      )`,
                    }}
                  >
                    {/* Step Number */}
                    <div
                      className="absolute -top-4 -left-4 w-10 h-10 rounded-full 
                                flex items-center justify-center bg-white/10 backdrop-blur-md 
                                border border-white/20 text-xl font-bold
                                transform transition-all duration-500 group-hover:scale-110 
                                group-hover:rotate-12"
                    >
                      {idx + 1}
                    </div>

                    {/* Icon Container */}
                    <div className="relative mb-4 ml-6">
                      <div
                        className="absolute -inset-2 bg-white/20 rounded-full blur-md opacity-0 
                                  group-hover:opacity-100 transition-opacity duration-500"
                      />
                      <EditableDiv
                        text={step.icon}
                        onEdit={(value) => handleStepEdit(idx, "icon", value)}
                        className="text-4xl transform transition-all duration-500 
                                group-hover:scale-110 group-hover:rotate-12"
                      />
                    </div>

                    {/* Title */}
                    <EditableDiv
                      text={step.title}
                      onEdit={(value) => handleStepEdit(idx, "title", value)}
                      className="text-xl font-bold mb-3 bg-gradient-to-r from-white to-green-100 
                              bg-clip-text text-transparent"
                    />

                    {/* Description */}
                    <EditableDiv
                      text={step.description}
                      onEdit={(value) =>
                        handleStepEdit(idx, "description", value)
                      }
                      className="text-base text-white/80 leading-relaxed
                              transform transition-all duration-300 group-hover:text-white"
                    />

                    {/* Progress Bar */}
                    <div className="absolute bottom-0 left-0 w-full h-1 overflow-hidden">
                      <div
                        className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent
                                  transform -translate-x-full group-hover:translate-x-full transition-transform 
                                  duration-1000"
                      />
                    </div>

                    {/* Connecting Arrow */}
                    {idx < parsedSteps.length - 1 && (
                      <div className="absolute top-1/2 -right-8 w-8 flex items-center">
                        <div className="flex-1 h-0.5 bg-gradient-to-r from-white/30 to-transparent" />
                        <div className="w-2 h-2 rounded-full bg-white/50 animate-pulse" />
                      </div>
                    )}

                    {/* Background Glow */}
                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 
                                transition-opacity duration-500 blur-xl -z-10"
                      style={{ background: step.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export default ProcessSlide1;
